import { metadata, constants, pageLoadStorage, uiRuntimeConfig, getRouter, rfidStrategy } from 'lib_ui-services';
import lodash from 'lodash';
const { cloneDeep } = lodash;
import { errors } from 'lib_ui-primitives';
import logging from '@sstdev/lib_logging';

const { useCaseIds } = constants;

const _p = {
    asyncConfirmationModal
};

export const _private = _p;

export default {
    verb: 'willCreate',
    namespace: 'item',
    relation: 'item',
    priority: 20,
    prerequisites: [],
    description: 'Copy tagId from assetNo if applicable',
    useCaseIds: [
        useCaseIds.ASSET_TRACKING,
        useCaseIds.RFID,
        useCaseIds.ITEM_TRACKING,
        useCaseIds.WISE_ID,
        useCaseIds.WORK_IN_PROCESS
    ],
    //this is the actual logic:
    logic
};

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").WorkflowStack} WorkflowStack
 * @typedef {import("rulesengine.io").Context} Context
 */

const FIVE_MINUTES = 300000;
/**
 * @template {{ newRecord: { [key: string]: any } }} T
 * @param {{
 *   data: T;
 *   prerequisiteResults: object[];
 *   context: Context;
 *   workflowStack: WorkflowStack[];
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 * @returns {Promise<T>}
 */
async function logic({ data, context, dispatch }) {
    try {
        const newRecord = cloneDeep(data.newRecord);
        const { namespace, relation } = context;

        // Apply configurable zero-trimming policy (ASCII 00-trim always, HEX leading-0 trim when enabled)
        const policy = rfidStrategy.computeZeroTrimPolicyFromConfig();

        // If the user requests that we assign a tag, display the PowerSliderPopup to get one.
        if (
            newRecord.tagId == null &&
            pageLoadStorage.getKey(`item:item.assignTag|${getRouter().getRouteStateKey()}`)
        ) {
            const { reads } = await dispatch(
                {},
                { namespace: 'application', relation: 'component', verb: 'open', type: 'PowerSliderPopup' },
                //defaultTimeout is production is 5 minutes as well, but just to make it not anoying in dev of localhost
                //because a timeout throws an error, and then just continues with the code below the catch without notice....
                //5 minutes _should_ be enough that in production its occurance should be rare enough
                //that we don't need to build in any additional logic to handle that neatly
                { awaitResult: true, timeOutMs: FIVE_MINUTES }
            );
            //validate read results
            if (reads.length < 1) {
                throw new errors.ValidationError('Assignment Failed: No RFID tag detected.', {});
            }
            reads.sort((a, b) => {
                if (a.rssi < b.rssi) return -1;
                if (a.rssi === b.rssi) return 0;
                return 1;
            });
            let tagId = reads[0].tagId;
            tagId = rfidStrategy.normalizeTagIdHex(tagId, { policy });

            const { result: tagLookupResult } = await dispatch(
                { tagId, 'meta.deleted': { $exists: false } },
                { verb: 'get', namespace: 'item', relation: 'item', type: 'find' },
                true
            );
            // If this tag is already assigned to a different asset.
            if (tagLookupResult.length > 0) {
                const assetNo = tagLookupResult[0].assetNo;
                // Trying to assign the asset to the same tag.
                if (assetNo === newRecord.assetNo) {
                    throw new errors.ValidationError(`This tag is already assigned to asset ${assetNo}.`, {});
                }
                const shouldProceed = await _p.asyncConfirmationModal(dispatch, assetNo, newRecord.assetNo);
                if (!shouldProceed) {
                    throw new errors.ValidationError('Tag assignment cancelled.', {});
                }

                // Remove this tag's assignment from other assets that have it.
                await Promise.all(
                    tagLookupResult.map(async item => {
                        const otherRecordThatHadTag = cloneDeep(item);
                        delete otherRecordThatHadTag.tagId;
                        await dispatch(
                            {
                                oldRecord: item,
                                newRecord: otherRecordThatHadTag,
                                propertiesToUnset: [{ propertyName: 'tagId' }]
                            },
                            { verb: 'update', namespace: 'item', relation: 'item' },
                            true
                        );
                    })
                );
            }

            newRecord.tagId = tagId;
            dispatch(
                {
                    isError: false, //todo: gl2025 remove
                    variant: 'info',
                    message: `Assigning tag ID ${newRecord.tagId}.`,
                    timeout: uiRuntimeConfig.getNotificationTimeout(),
                    type: constants.notificationTypes.TOAST
                },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
        } else if (
            [constants.useCaseIds.ITEM_TRACKING, constants.useCaseIds.WORK_IN_PROCESS].includes(
                context.session.useCase['metaui:useCase']._id
            )
        ) {
            const titleAlternative = metadata.getTitleAlternative(namespace, relation, 'title');
            newRecord.tagId = newRecord.tagId ?? rfidStrategy.getTagId(newRecord[titleAlternative]);
        }

        // include the updated newRecord in the data before passing it to the getOfflineAction method
        data.newRecord = newRecord;
        return data;
    } catch (err) {
        if (err instanceof errors.ValidationError) {
            dispatch(
                {
                    isError: true, //todo: gl2025 remove
                    variant: 'error',
                    message: err.message,
                    timeout: uiRuntimeConfig.getNotificationTimeout(),
                    type: constants.notificationTypes.TOAST
                },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
        } else if (err?.form?.[0] !== null) {
            dispatch(
                {
                    isError: true, //todo: gl2025 remove
                    variant: 'error',
                    message: err.form[0],
                    timeout: uiRuntimeConfig.getNotificationTimeout(),
                    type: constants.notificationTypes.TOAST
                },
                { verb: 'pop', namespace: 'application', relation: 'notification' }
            );
            throw new Error(err.form[0]);
        } else {
            logging.error(err);
        }
        throw err;
    }
}

async function asyncConfirmationModal(dispatch, oldAssetNo, newAssetNo) {
    return new Promise(resolve => {
        const message2 =
            newAssetNo != null
                ? `Are you sure you want to remove it, and assign it to asset ${newAssetNo}?`
                : 'Are you sure you want to remove it, and assign it to a new asset?';
        dispatch(
            {
                message: [`This tag is currently assigned to asset ${oldAssetNo}.`, message2],
                okButtonText: 'YES',
                icon: 'warning',
                iconColorStyle: constants.COLOR_STYLES.Warning,
                variant: constants.VARIANT.Warning,
                okAction: () => resolve(true),
                cancelAction: () => resolve(false)
            },
            { verb: 'confirm', namespace: 'application', relation: 'user' }
        );
    });
}
